import posthog from 'posthog-js';
import config from '../config/environment';
import {
  isPublicEditorPath,
  redirectToAutomateSignIn,
} from './dashboardAuthParams';

// Initialize PostHog
let isInitialized = false;

export const initPostHog = () => {
  if (isInitialized) return;
  
  if (typeof window !== 'undefined' && config.POSTHOG_API_KEY) {
    posthog.init(config.POSTHOG_API_KEY, {
      api_host: config.POSTHOG_HOST,
      loaded: () => {},
      capture_pageview: true, // Automatically capture pageviews
      capture_pageleave: true, // Automatically capture pageleave events
    });
    isInitialized = true;
  }
};

/** Identify from an already-fetched /me payload (avoids a second /me on boot). */
export const identifyUserFromMe = (userData) => {
  if (!isInitialized || !userData || !userData.email) return;
  posthog.identify(userData.id?.toString() || userData.email, {
    email: userData.email,
    name: userData.name,
    user_id: userData.id,
    tenant_id: userData.tenants?.[0]?.tenant?.id,
  });
};

// Identify user with email (fetches /me; redirects on non-200 for protected routes)
export const identifyUser = async (token) => {
  if (!token || !isInitialized) return;
  
  try {
    const response = await fetch('https://backend.workload.co/api/auth/me', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.status !== 200) {
      // Mid-session expired JWT (e.g. AppBar re-identify) — same Automate handoff as boot.
      if (!isPublicEditorPath()) {
        try {
          localStorage.removeItem('tok_');
        } catch (_) {
          /* ignore */
        }
        redirectToAutomateSignIn();
      }
      return;
    }

    const userData = await response.json();
    identifyUserFromMe(userData);
  } catch (error) {
    console.error('Error identifying user in PostHog:', error);
  }
};

// Track workflow events
export const trackWorkflowEvent = (eventName, properties = {}) => {
  if (!isInitialized) return;
  posthog.capture(eventName, properties);
};

// Track workflow created
export const trackWorkflowCreated = (workflowId, workflowName) => {
  trackWorkflowEvent('workflow_created', {
    workflow_id: workflowId,
    workflow_name: workflowName || 'Untitled Workflow'
  });
};

// Track workflow loaded
export const trackWorkflowLoaded = (workflowId, workflowName) => {
  trackWorkflowEvent('workflow_loaded', {
    workflow_id: workflowId,
    workflow_name: workflowName || 'Untitled Workflow'
  });
};

// Track app selected
export const trackAppSelected = (appName, appType, stepType) => {
  trackWorkflowEvent('app_selected', {
    app_name: appName,
    app_type: appType,
    step_type: stepType // 'trigger' or 'action'
  });
};

// Track event selected
export const trackEventSelected = (appName, eventName, stepType) => {
  trackWorkflowEvent('event_selected', {
    app_name: appName,
    event_name: eventName,
    step_type: stepType
  });
};

// Track field mapped
export const trackFieldMapped = (fieldName, fieldValue, stepType) => {
  trackWorkflowEvent('field_mapped', {
    field_name: fieldName,
    field_value_type: typeof fieldValue,
    step_type: stepType
  });
};

// Track credential connected
export const trackCredentialConnected = (appName, credentialId) => {
  trackWorkflowEvent('credential_connected', {
    app_name: appName,
    credential_id: credentialId
  });
};

// Track test data created
export const trackTestDataCreated = (stepType) => {
  trackWorkflowEvent('test_data_created', {
    step_type: stepType
  });
};

// Track workflow activated (options can include source: 'ai' when workflow was AI-generated)
export const trackWorkflowActivated = (workflowId, workflowName, options = {}) => {
  trackWorkflowEvent('workflow_activated', {
    workflow_id: workflowId,
    workflow_name: workflowName,
    ...options
  });
};

// Track workflow deactivated
export const trackWorkflowDeactivated = (workflowId, workflowName) => {
  trackWorkflowEvent('workflow_deactivated', {
    workflow_id: workflowId,
    workflow_name: workflowName
  });
};

// --- Validation / activation gating (lightweight; no full payloads) ---

/** workflow_id, issue_types[] only */
export const trackWorkflowValidationFailed = (workflowId, issueTypes = []) => {
  trackWorkflowEvent('workflow_validation_failed', {
    workflow_id: workflowId ?? undefined,
    issue_types: Array.isArray(issueTypes) ? issueTypes : [],
  });
};

/** workflow_id, app_name only */
export const trackWorkflowNeedsUserAuth = (workflowId, appName) => {
  trackWorkflowEvent('workflow_needs_user_auth', {
    workflow_id: workflowId ?? undefined,
    app_name: appName ?? undefined,
  });
};

/** workflow_id, count only */
export const trackWorkflowRepairAttempted = (workflowId, count) => {
  trackWorkflowEvent('workflow_repair_attempted', {
    workflow_id: workflowId ?? undefined,
    count: typeof count === 'number' ? count : 1,
  });
};

/** workflow_id only */
export const trackWorkflowRepairSucceeded = (workflowId) => {
  trackWorkflowEvent('workflow_repair_succeeded', {
    workflow_id: workflowId ?? undefined,
  });
};

/** workflow_id, issue_types[] only */
export const trackWorkflowActivationBlocked = (workflowId, issueTypes = []) => {
  trackWorkflowEvent('workflow_activation_blocked', {
    workflow_id: workflowId ?? undefined,
    issue_types: Array.isArray(issueTypes) ? issueTypes : [],
  });
};

// --- AI workflow metrics (for % generated / activated vs before) ---

// AI generation succeeded (workflow returned from Lambda with source: 'ai')
export const trackAIGenerationSuccess = (properties = {}) => {
  trackWorkflowEvent('ai_workflow_generated', {
    source: 'ai',
    ...properties
  });
};

// AI generation failed or fell back to non-AI
export const trackAIGenerationFailed = (reason = 'unknown', properties = {}) => {
  trackWorkflowEvent('ai_workflow_generation_failed', {
    reason,
    ...properties
  });
};

// User applied an AI-generated workflow to the canvas
export const trackAIWorkflowApplied = (properties = {}) => {
  trackWorkflowEvent('ai_workflow_applied', {
    ...properties
  });
};

// --- AI Agent funnel: user prompt → AI generated workflow → applied → activated ---
// Use funnel_session_id to correlate events in PostHog (same session = same flow attempt).

export const createFunnelSessionId = (workflowId = null) => {
  return `ai_funnel_${Date.now()}_${workflowId ?? 'new'}`;
};

// Step 1: User sent a prompt to the AI agent
export const trackAIAgentPromptSent = (properties = {}) => {
  trackWorkflowEvent('ai_agent_prompt_sent', {
    ...properties
  });
};

// Step 2a: Agent successfully generated a workflow (chunk.type === 'workflow')
export const trackAIAgentWorkflowGenerated = (properties = {}) => {
  trackWorkflowEvent('ai_agent_workflow_generated', {
    ...properties
  });
};

// Step 2b: Agent flow failed (error chunk or generation failed)
export const trackAIAgentError = (properties = {}) => {
  trackWorkflowEvent('ai_agent_error', {
    ...properties
  });
};

// Substeps within "AI system generated workflow" (where the funnel can break)
export const trackAIAgentAuthNeeded = (properties = {}) => {
  trackWorkflowEvent('ai_agent_auth_needed', {
    ...properties
  });
};

export const trackAIAgentFieldSetupShown = (properties = {}) => {
  trackWorkflowEvent('ai_agent_field_setup_shown', {
    ...properties
  });
};

export const trackAIAgentFieldMapped = (properties = {}) => {
  trackWorkflowEvent('ai_agent_field_mapped', {
    ...properties
  });
};

export const trackAIAgentTestStepRequested = (properties = {}) => {
  trackWorkflowEvent('ai_agent_test_step_requested', {
    ...properties
  });
};

export const trackAIAgentTestStepComplete = (properties = {}) => {
  trackWorkflowEvent('ai_agent_test_step_complete', {
    ...properties
  });
};

// Track step completed
export const trackStepCompleted = (stepType, stepName) => {
  trackWorkflowEvent('step_completed', {
    step_type: stepType,
    step_name: stepName
  });
};

// Track error
export const trackError = (errorType, errorMessage, context = {}) => {
  trackWorkflowEvent('workflow_error', {
    error_type: errorType,
    error_message: errorMessage,
    ...context
  });
};

// Track validation issue
export const trackValidationIssue = (issueType, stepType, details = {}) => {
  trackWorkflowEvent('validation_issue', {
    issue_type: issueType,
    step_type: stepType,
    ...details
  });
};

// Track page view
export const trackPageView = (pageName) => {
  trackWorkflowEvent('$pageview', {
    page_name: pageName
  });
};

export default posthog;

